// @flow

import React, { createContext, useContext, type Node } from 'react';
import type { IncomingPayment, PendingIncomingPayment, OpenedIncomingPayment, ConfirmedIncomingPayment } from './types';

const IncomingPaymentContext = createContext<?IncomingPayment>(null);

type Props = {
  incomingPayment: IncomingPayment,
  children: Node,
};

export function IncomingPaymentProvider({ incomingPayment, children }: Props): Node {
  return (
    <IncomingPaymentContext.Provider value={incomingPayment}>
      {children}
    </IncomingPaymentContext.Provider>
  );
}

export function useIncomingPayment(): IncomingPayment {
  const context = useContext(IncomingPaymentContext);
  if (!context) {
    throw new Error('IncomingPaymentContext was not provided');
  }
  return context;
}

export function usePendingIncomingPayment(): PendingIncomingPayment {
  const incomingPayment = useIncomingPayment();
  if (incomingPayment.status !== 'PE')
    throw new Error(`IncomingPayment: invalid status: "${incomingPayment.status}". It should be PE.`);
  return incomingPayment;
}

export function useOpenedIncomingPayment(): OpenedIncomingPayment {
  const incomingPayment = useIncomingPayment();
  if (incomingPayment.status !== 'OP')
    throw new Error(`IncomingPayment: invalid status: "${incomingPayment.status}". It should be OP.`);
  return incomingPayment;
}

export function useConfirmedIncomingPayment(): ConfirmedIncomingPayment {
  const incomingPayment = useIncomingPayment();
  if (incomingPayment.status !== 'CO')
    throw new Error(`IncomingPayment: invalid status: "${incomingPayment.status}". It should be CO.`);
  return incomingPayment;
}

const RequestReloadIncomingPaymentContext = createContext<?() => void>(null);

export const RequestReloadIncomingPaymentProvider = RequestReloadIncomingPaymentContext.Provider;

export function useRequestReloadIncomingPaymentContext(): () => void {
  const requestReloadIncomingPaymentContext = useContext(RequestReloadIncomingPaymentContext);
  if (!requestReloadIncomingPaymentContext) {
    throw new Error('RequestReloadIncomingPaymentContext was not provided');
  }
  return requestReloadIncomingPaymentContext;
}
